<script setup lang="ts">
  import { computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Archive, Sparkles, Undo2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useMAssetLibraryStore } from "@stores/mAssetLibrary";
  import ImageLibraryAssetThumbnail from "@/components/MCampaign/ImageLibrary/ImageLibraryAssetThumbnail.vue";
  import ImageLibraryAssetDetails from "@/components/MCampaign/ImageLibrary/ImageLibraryAssetDetails.vue";
  import type { MMediumUpload } from "@/types";

  const assetLibraryStore = useMAssetLibraryStore();
  const { editedAsset, allAssetTags, isSavingEditedAsset, membership } = storeToRefs(assetLibraryStore);
  const { setEditedAsset, updateAsset, hideFromLibrary, restoreToLibrary, hasMissingName, hasMissingTags, suggestTags } =
    assetLibraryStore;

  const asset = computed(() => editedAsset.value as MMediumUpload);

  const tagOptions = computed(() => allAssetTags.value.map((tag) => ({ value: tag.name, label: tag.name })));

  const hasError = computed(() => hasMissingName(asset.value) || hasMissingTags(asset.value));

  const close = () => setEditedAsset(null);

  const save = async () => {
    await updateAsset(asset.value);
    close();
  };

  const isSuggesting = ref(false);
  const suggestionHint = ref("");

  const addSuggestedTags = async () => {
    isSuggesting.value = true;
    suggestionHint.value = "";
    try {
      const current = asset.value.asset_tags ?? [];
      const suggested = (await suggestTags(asset.value.id)).filter((tag) => !current.includes(tag));
      asset.value.asset_tags = [...current, ...suggested];
      suggestionHint.value = suggested.length ? `${suggested.length} suggested — review them, then save.` : "No other tag fits.";
    } finally {
      isSuggesting.value = false;
    }
  };

  const takeOut = () => hideFromLibrary([asset.value.id]);

  const putBack = () => restoreToLibrary([asset.value.id]);
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="flex-1 space-y-4 overflow-y-auto p-4">
      <div class="relative aspect-square overflow-hidden rounded-md bg-muted">
        <ImageLibraryAssetThumbnail
          :asset="asset"
          fit="contain"
          playable />
      </div>

      <div class="space-y-1">
        <label class="text-xs font-medium text-muted-foreground">Name</label>
        <Input v-model="asset.name" />
      </div>

      <div class="space-y-1">
        <div class="flex items-center justify-between">
          <label class="text-xs font-medium text-muted-foreground">Tags</label>
          <Button
            v-if="asset.media_type === 'image'"
            variant="ghost"
            size="sm"
            class="h-6 px-2 text-xs"
            :disabled="isSuggesting"
            @click="addSuggestedTags">
            <Sparkles class="mr-1 h-3.5 w-3.5" />
            {{ isSuggesting ? "Looking at the image…" : "Suggest tags" }}
          </Button>
        </div>
        <Select
          v-model="asset.asset_tags"
          :options="tagOptions"
          multiple
          filterable
          creatable
          placeholder="Add tags" />
        <p
          v-if="suggestionHint"
          class="text-xs text-muted-foreground">
          {{ suggestionHint }}
        </p>
      </div>

      <p class="text-xs text-muted-foreground">
        Used in {{ asset.used_in_creatives_count ?? 0 }} creative(s).
        Edits apply across every creative that uses this asset.
      </p>

      <ImageLibraryAssetDetails :asset="asset" />

      <Button
        v-if="membership === 'out'"
        variant="outline"
        class="w-full"
        @click="putBack">
        <Undo2 class="mr-1 h-4 w-4" />
        Put back in library
      </Button>
      <ConfirmDialog
        v-else
        title="Remove from library?"
        confirm-label="Remove"
        @confirm="takeOut">
        <template #trigger>
          <Button
            variant="outline"
            class="w-full text-status-error-strong">
            <Archive class="mr-1 h-4 w-4" />
            Remove from library
          </Button>
        </template>
        "{{ asset.name }}" stays in the campaigns already using it, and you can put it back later.
      </ConfirmDialog>
    </div>

    <div class="flex items-center justify-end gap-2 border-t px-4 py-3">
      <Button
        variant="ghost"
        @click="close">
        Cancel
      </Button>
      <Button
        :disabled="isSavingEditedAsset || hasError"
        @click="save">
        Save
      </Button>
    </div>
  </div>
</template>
